import { useEffect, useId, useState } from 'react'
import { Button, Checkbox, Dialog, Notice, Select, TextField } from '@/components/play'
import { apiErrors, createAthlete, updateAthlete, uploadPhoto } from '@/lib/athletes/api'
import type { AthleteFields, AthleteRecord, Gender, Race } from '@/lib/athletes/types'

interface Props {
  eventId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The athlete to change; null adds one. */
  athlete: AthleteRecord | null
  races: Race[]
  /** Whether the event has a tracking source, so a tracker id can be saved. */
  canTrack: boolean
  onSaved: (athlete: AthleteRecord) => void
}

const NONE = 'none'

/** Adds or changes one athlete: who they are, their race, their tracker, a photo, and whether the public sees them. */
export function AthleteDialog({ eventId, open, onOpenChange, athlete, races, canTrack, onSaved }: Props) {
  const formId = useId()
  const currentTracker = athlete?.trackers[0]?.external_ref ?? ''
  const [name, setName] = useState('')
  const [bib, setBib] = useState('')
  const [gender, setGender] = useState<Gender | typeof NONE>(NONE)
  const [race, setRace] = useState<string>(NONE)
  const [tracker, setTracker] = useState('')
  const [hidden, setHidden] = useState(false)
  const [photo, setPhoto] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(athlete?.name ?? '')
    setBib(athlete?.bib ?? '')
    setGender(athlete?.gender ?? NONE)
    setRace(athlete?.route_id ?? NONE)
    setTracker(currentTracker)
    setHidden(athlete?.hidden ?? false)
    setPhoto(null)
    setError('')
  }, [open, athlete, currentTracker])

  async function save(event: React.FormEvent) {
    event.preventDefault()
    if (saving) return
    setSaving(true)
    setError('')
    const fields: AthleteFields = {
      name: name.trim(),
      bib: bib.trim() || null,
      gender: gender === NONE ? null : gender,
      route_id: race === NONE ? null : race,
      hidden,
    }
    if (tracker.trim() !== currentTracker) fields.tracker_ref = tracker.trim() || null
    try {
      let saved = athlete ? await updateAthlete(eventId, athlete.id, fields) : await createAthlete(eventId, fields)
      if (photo) saved = await uploadPhoto(eventId, saved.id, photo)
      onSaved(saved)
      onOpenChange(false)
    } catch (failure) {
      setError(apiErrors(failure, 'The athlete couldn’t be saved. Try again.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={athlete ? `Edit ${athlete.name}` : 'Add an athlete'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={saving}>
            {athlete ? 'Save athlete' : 'Add athlete'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={save} className="space-y-5">
        {error && (
          <Notice tone="error" alert>
            {error}
          </Notice>
        )}
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
        <TextField label="Bib" value={bib} onChange={(e) => setBib(e.target.value)} optional autoComplete="off" />
        <Select
          label="Gender"
          items={[
            { value: NONE, label: 'Not set' },
            { value: 'female', label: 'Female' },
            { value: 'male', label: 'Male' },
          ]}
          value={gender}
          onValueChange={(value) => setGender((value as Gender | null) ?? NONE)}
        />
        <Select
          label="Race"
          description="The course they run. Their dot takes its colour on the map."
          items={[{ value: NONE, label: 'No race' }, ...races.map((each) => ({ value: each.id, label: each.name }))]}
          value={race}
          onValueChange={(value) => setRace(value ?? NONE)}
        />
        <TextField
          label="Tracker id"
          value={tracker}
          onChange={(e) => setTracker(e.target.value)}
          optional
          mono
          autoComplete="off"
          disabled={!canTrack && !currentTracker}
          description={canTrack ? 'The id your tracking provider uses, such as LNX’s trackerId.' : 'Connect a tracking provider first.'}
        />
        <TextField
          label="Photo"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          optional
          description="PNG, JPEG or WebP, up to 5 MB."
          onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
        />
        <Checkbox
          label="Hide from the public page"
          description="They stay on your roster, and off the event map."
          checked={hidden}
          onCheckedChange={(checked) => setHidden(checked)}
        />
      </form>
    </Dialog>
  )
}
